const DRAFT_STORAGE_KEY = 'seller-onboarding-draft-v2';

export const sellerOnboardingComputed = {
    selectedAccount() {
        return this.state.accounts.find(account => account.id === this.selectedAccountId) || null;
    },
    selectedShop() {
        return this.selectedAccount?.shops.find(shop => shop.id === this.selectedShopId) || null;
    },
    selectedSeller() {
        return this.selectedAccount?.sellers?.find(seller => seller.id === this.selectedSellerId) || null;
    },
    canCreateShop() {
        return this.createNewAccount || Boolean(this.selectedAccount?.service_limits.shops.can_create);
    },
    selectedModules() {
        return [
            ...(this.moduleShop ? ['shop'] : []),
            ...(this.moduleSeller ? ['seller'] : []),
            ...(this.moduleDelivery ? ['delivery_service'] : []),
        ];
    },
    progressSteps() {
        return [
            {key: 'profile', title: 'Профиль'},
            {key: 'account', title: 'Бизнес'},
            {key: 'modules', title: 'Модули'},
            {key: 'ready', title: 'Готово'},
        ];
    },
    currentStepIndex() {
        return Math.max(0, this.progressSteps.findIndex(progressStep => progressStep.key === this.step));
    },
};

export const sellerOnboardingStateMethods = {
    applyState(state) {
        this.state = state;

        if (!this.selectedAccountId || !this.state.accounts.some(account => account.id === this.selectedAccountId)) {
            this.selectedAccountId = this.state.accounts[0]?.id || null;
        }

        if (this.selectedAccount) {
            this.createNewAccount = false;
            this.prepareSelectedAccount();
        } else {
            this.createNewAccount = true;
            this.selectedShopId = null;
            this.createNewShop = true;
        }

        if (!this.accountForm.email && this.state.user?.email) {
            this.accountForm.email = this.state.user.email;
        }

        this.persistDraft();
    },
    switchProfileMode(mode) {
        this.profileMode = mode;
        this.clearErrors();
    },
    startNewAccount() {
        this.createNewAccount = true;
        this.selectedShopId = null;
        this.createNewShop = true;
        this.clearErrors();
    },
    cancelNewAccount() {
        this.createNewAccount = false;
        this.selectedAccountId = this.selectedAccountId || this.state.accounts[0]?.id || null;
        this.prepareSelectedAccount();
        this.clearErrors();
    },
    prepareSelectedAccount() {
        if (!this.selectedAccount) {
            return;
        }

        if (!this.selectedShopId || !this.selectedAccount.shops.some(shop => shop.id === this.selectedShopId)) {
            this.selectedShopId = this.selectedAccount.shops[0]?.id || null;
        }
        this.createNewShop = this.selectedAccount.shops.length === 0;
        this.persistDraft();
    },
    startNewShop() {
        this.createNewShop = true;
        this.selectedShopId = null;
        this.errors = {};
    },
    chooseExistingShop() {
        this.createNewShop = false;
        this.selectedShopId = this.selectedAccount?.shops[0]?.id || null;
        this.errors = {};
    },
    capabilityRequest(serviceType) {
        return this.selectedAccount?.service_requests.find(request => request.service_type === serviceType) || null;
    },
    capabilityLimit(serviceType) {
        const key = serviceType === 'seller' ? 'sellers' : 'delivery_services';

        return this.selectedAccount?.service_limits[key] || {limit: 0, used: 0};
    },
    capabilityStatus(serviceType) {
        if (serviceType === 'seller') {
            return !this.createNewAccount && this.selectedAccount?.sellers?.length ? 'Уже создан' : 'Создадим сразу';
        }

        if (this.createNewAccount) {
            return 'Запросим доступ';
        }

        const limit = this.capabilityLimit(serviceType);
        const request = this.capabilityRequest(serviceType);

        if (limit.limit > 0) {
            return 'Уже доступно';
        }
        if (request?.status === 'pending') {
            return 'Запрос уже отправлен';
        }
        if (request?.status === 'approved') {
            return 'Разрешено';
        }
        if (request?.status === 'rejected') {
            return 'Можно запросить повторно';
        }

        return 'Запросим доступ';
    },
    capabilityStatusClass(serviceType) {
        if (serviceType === 'seller') {
            return 'text-success';
        }

        const request = this.capabilityRequest(serviceType);

        if (this.capabilityLimit(serviceType).limit > 0 || request?.status === 'approved') {
            return 'text-success';
        }
        if (request?.status === 'pending') {
            return 'text-warning-emphasis';
        }

        return 'text-secondary';
    },
    capabilityCompletionText(serviceType) {
        if (serviceType === 'seller') {
            return 'Отдел продаж доступен в бизнес-аккаунте.';
        }

        const request = this.capabilityRequest(serviceType);

        if (this.capabilityLimit(serviceType).limit > 0 || request?.status === 'approved') {
            return 'Возможность доступна в бизнес-аккаунте.';
        }
        if (request?.status === 'rejected') {
            return request.review_comment || 'Запрос отклонён.';
        }

        return 'Запрос ожидает решения администрации.';
    },
    startAnotherSetup() {
        this.resetModules();
        this.createNewAccount = this.state.accounts.length === 0;
        this.selectedAccountId = this.state.accounts[0]?.id || null;
        this.selectedSellerId = null;
        this.prepareSelectedAccount();
        this.step = 'account';
        this.persistDraft();
    },
    resetModules() {
        this.moduleShop = false;
        this.moduleSeller = false;
        this.moduleDelivery = false;
        this.shopForm.name = '';
    },
    progressStepClass(stepKey) {
        const index = this.progressSteps.findIndex(progressStep => progressStep.key === stepKey);

        return {
            active: stepKey === this.step,
            completed: index < this.currentStepIndex,
        };
    },
    goToCompletedStep(stepKey, index) {
        if (index <= this.currentStepIndex && stepKey !== 'ready') {
            this.step = stepKey;
            this.clearErrors();
        }
    },
    clearErrors() {
        this.errors = {};
        this.generalError = null;
    },
    handleError(error) {
        this.errors = error.response?.data?.errors || {};
        this.generalError = error.response?.data?.message && !Object.keys(this.errors).length
            ? error.response.data.message
            : (!error.response ? 'Не удалось связаться с сервером. Попробуй ещё раз.' : null);
    },
    restoreDraft() {
        try {
            const draft = JSON.parse(localStorage.getItem(DRAFT_STORAGE_KEY) || '{}');
            this.selectedAccountId = draft.selectedAccountId || null;
            this.selectedShopId = draft.selectedShopId || null;
        } catch {
            localStorage.removeItem(DRAFT_STORAGE_KEY);
        }
    },
    persistDraft() {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({
            selectedAccountId: this.selectedAccountId,
            selectedShopId: this.selectedShopId,
        }));
    },
};
